import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PurchaseInvoiceDocument } from '@kontor/shared';
import { ApiError } from '../../api';
import './purchase.css';

const ROLE_LABELS: Record<PurchaseInvoiceDocument['role'], string> = { original: 'Original', einvoice_xml: 'E-Rechnung (XML)', attachment: 'Anlage' };
/** Was der Browser selbst anzeigen kann. HEIC und XML bleiben beim Herunterladen. */
const VIEWABLE = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);

const kb = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`);

async function fetchBlob(invoiceId: string, doc: PurchaseInvoiceDocument): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/invoices-in/${invoiceId}/documents/${doc.id}/download`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'network', 'Keine Verbindung zum Server.');
  }
  if (!res.ok) throw new ApiError(res.status, 'download', 'Der Beleg konnte nicht geladen werden.');
  return new Blob([await res.arrayBuffer()], { type: doc.contentType });
}

/**
 * Beleg neben den Feldern (KON-142): PDF oder Foto der Rechnung. Der Server liefert Dateien nur zum Herunterladen, deshalb holt die Seite sie selbst und zeigt sie
 * aus dem Arbeitsspeicher an. Bei mehreren Dateien (zweite Seite, XML) wählt man oben die Datei.
 */
export function PurchaseDocumentViewer({ invoiceId, documents, onRemove, removable }: { invoiceId: string; documents: PurchaseInvoiceDocument[]; onRemove?: (doc: PurchaseInvoiceDocument) => void; removable?: (doc: PurchaseInvoiceDocument) => boolean }) {
  const viewable = documents.filter((d) => VIEWABLE.has(d.contentType));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const current = documents.find((d) => d.id === selectedId) ?? viewable[0] ?? documents[0];
  const show = current && VIEWABLE.has(current.contentType);

  const blob = useQuery({
    queryKey: ['purchase-document', invoiceId, current?.id],
    queryFn: () => fetchBlob(invoiceId, current!),
    enabled: !!current && !!show,
    staleTime: Infinity,
    gcTime: 60_000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) {
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob.data);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob.data]);

  const download = useMutation({
    mutationFn: async (doc: PurchaseInvoiceDocument) => {
      const file = await fetchBlob(invoiceId, doc);
      const link = document.createElement('a');
      const href = URL.createObjectURL(file);
      link.href = href;
      link.download = doc.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
    },
  });

  if (documents.length === 0) {
    return <p className="muted">Noch kein Beleg. Mit „Beleg hinzufügen“ hängen Sie die PDF oder ein Foto der Rechnung an, dann steht sie neben den Feldern.</p>;
  }
  return (
    <div className="viewer">
      {documents.length > 1 && (
        <div className="viewer__tabs" role="tablist" aria-label="Belege">
          {documents.map((d) => (
            <button key={d.id} type="button" role="tab" aria-selected={d.id === current?.id} className={`viewer__tab${d.id === current?.id ? ' is-active' : ''}`} onClick={() => setSelectedId(d.id)}>
              {ROLE_LABELS[d.role]} · {d.fileName}
            </button>
          ))}
        </div>
      )}
      {current && (
        <div className="viewer__bar">
          <span className="small muted">{current.fileName} · {kb(current.sizeBytes)}</span>
          <span className="viewer__actions">
            <button type="button" className="btn btn--secondary btn--sm" disabled={download.isPending} onClick={() => download.mutate(current)}>Herunterladen</button>
            {onRemove && (!removable || removable(current)) && (
              <button type="button" className="btn btn--secondary btn--sm" aria-label={`${current.fileName} entfernen`} onClick={() => window.confirm('Diesen Beleg vom Entwurf entfernen? Im Belegarchiv bleibt die Datei erhalten.') && onRemove(current)}>Entfernen</button>
            )}
          </span>
        </div>
      )}
      {show && blob.isLoading && <p className="muted" role="status">Beleg wird geladen …</p>}
      {show && blob.isError && <div className="alert alert--error" role="alert">{(blob.error as Error).message}</div>}
      {show && url && current.contentType === 'application/pdf' && <iframe className="viewer__frame" src={url} title={`Beleg ${current.fileName}`} />}
      {show && url && current.contentType.startsWith('image/') && <img className="viewer__image" src={url} alt={`Beleg ${current.fileName}`} />}
      {current && !show && <p className="muted">{current.contentType === 'application/xml' ? 'Das ist das maschinenlesbare XML der E-Rechnung. Wirkia hat die Felder daraus gelesen.' : 'Dieses Format zeigt der Browser nicht an.'} Mit „Herunterladen“ öffnen Sie die Datei.</p>}
      {download.isError && <div className="alert alert--error" role="alert">Der Beleg konnte nicht geladen werden.</div>}
    </div>
  );
}
